import { View } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { HorizontalSnapCarousel } from "@src/packages/homepage/plan/components/HorizontalSnapCarousel";
import {
  Activity,
  AlertTriangle,
  Baby,
  Beaker,
  BookOpen,
  Calendar,
  Check,
  ClipboardList,
  Dna,
  Droplet,
  Heart,
  Hourglass,
  Info,
  Pill,
  Sparkles,
  Syringe,
  Target,
  Zap,
  ClipboardCheck,
  type LucideIcon,
} from "lucide-react-native";
import { getMoonHubStepPhase } from "./stepGuidancePhase";
import type {
  StepPerspective,
  TreatmentStepConfig,
} from "@src/packages/homepage/sections/TreatmentProgressModal";
import {
  getMoonPhaseContent,
  getMoonPhaseContentForPerspective,
  getMoonPhaseContentMap,
  type CycleContentContext,
} from "./moonPhaseContent";
import { useTranslations } from "@src/packages/i18n";
import type { TranslationFunction } from "@src/packages/i18n";

function tIndexedList(
  t: TranslationFunction,
  baseKey: string,
  maxItems = 8,
): string[] {
  const items: string[] = [];
  for (let i = 0; i < maxItems; i++) {
    const key = `${baseKey}.${i}`;
    const value = t(key);
    if (value === key) break;
    items.push(value);
  }
  return items;
}

const TEXT_MID = "#5A4D45";
const TEXT_LIGHT = "#8A7D74";
const WARNING = "#B08030";
const CARD_MIN_HEIGHT = 284;

type StepContentOverride = {
  description?: string;
  howItWorks?: string;
  whatToExpect?: string[];
  prepInstructions?: string[];
  whenToCall?: string[];
  emotionalSupport?: string;
};

function buildStepContent(
  t: TranslationFunction,
  key: string,
  hasWhenToCall = true,
): StepContentOverride {
  const base = `care.guidance.step.${key}`;
  return {
    description: t(`${base}.description`),
    howItWorks: t(`${base}.how_it_works`),
    whatToExpect: tIndexedList(t, `${base}.what_to_expect`),
    prepInstructions: tIndexedList(t, `${base}.prep_instructions`),
    whenToCall: hasWhenToCall
      ? tIndexedList(t, `${base}.when_to_call`)
      : undefined,
  };
}

function getStepContentOverride(
  t: TranslationFunction,
  step: TreatmentStepConfig,
  cycleContext?: CycleContentContext,
): StepContentOverride | undefined {
  const cycleType = cycleContext?.cycleType;
  const isIvfLike = cycleType === "ivf" || cycleType === "icsi";

  if (step.id === "sperm_collection") {
    return buildStepContent(t, "sperm_collection", false);
  }

  if (step.id === "pgt_testing") {
    return buildStepContent(t, "pgt", false);
  }

  if (step.id === "freezing") {
    if (cycleType === "sperm_freezing") {
      return buildStepContent(t, "freezing_sperm");
    }
    if (cycleType === "embryo_freezing") {
      return buildStepContent(t, "freezing_embryo");
    }
    return buildStepContent(t, "freezing_eggs");
  }

  if (step.id === "fertilization") {
    if (cycleType === "embryo_freezing") {
      return buildStepContent(t, "fertilization_embryo");
    }
    if (cycleType === "icsi") {
      return buildStepContent(t, "fertilization_icsi");
    }
    if (cycleType === "ivf") {
      return buildStepContent(t, "fertilization_ivf");
    }
  }

  if (step.id === "embryo_development" && isIvfLike) {
    return buildStepContent(t, "embryo_development");
  }

  if (step.id === "transfer") {
    if (cycleType === "fet") {
      return buildStepContent(t, "transfer_fet");
    }
    if (isIvfLike) {
      return buildStepContent(t, "transfer_ivf");
    }
  }

  if (step.id === "analysis") {
    return buildStepContent(t, "analysis");
  }

  if (step.id === "screening") {
    return buildStepContent(t, "screening");
  }

  if (step.id === "insemination") {
    return buildStepContent(t, "insemination");
  }

  if (step.id === "protocol" && cycleType === "fet") {
    return buildStepContent(t, "protocol_fet");
  }

  if (step.id === "protocol" && cycleType === "oi") {
    return buildStepContent(t, "protocol_oi");
  }

  if (
    step.id === "luteal_phase" &&
    (cycleType === "ivf" || cycleType === "icsi" || cycleType === "fet")
  ) {
    return buildStepContent(t, "luteal_ivf");
  }

  if (
    step.id === "luteal_phase" &&
    (cycleType === "iui" || cycleType === "oi")
  ) {
    return buildStepContent(t, "luteal_iui");
  }

  // The outcome step maps to the `waiting` phase, whose copy is written for a
  // transfer — "After transfer, you'll continue progesterone support…". True for
  // IVF, ICSI and FET, and wrong for the two flows that never had a transfer, so
  // only those two override it. Leaving IVF-like on the phase copy is deliberate:
  // that block has a partner variant, and a step override would shadow it.
  if (step.id === "outcome" && (cycleType === "iui" || cycleType === "oi")) {
    return buildStepContent(t, "outcome_iui");
  }

  return undefined;
}

const STEP_ICONS: Record<string, LucideIcon> = {
  pre_treatment: ClipboardList,
  stimulation: Syringe,
  protocol: Syringe,
  trigger_shot: Zap,
  egg_retrieval: Sparkles,
  freezing: ClipboardCheck,
  fertilization: Beaker,
  embryo_development: Beaker,
  transfer: Baby,
  // The hourglass belongs to the wait, which is the outcome step now. Luteal
  // support is the medication.
  luteal_phase: Pill,
  outcome: Hourglass,
  sperm_collection: Beaker,
  insemination: Target,
  screening: ClipboardList,
  analysis: Beaker,
  pgt_testing: Dna,
};

type GuidanceContent = {
  howItWorks?: string;
  whatToExpect?: string[];
  prepInstructions?: string[];
  whenToCall?: string[];
  emotionalSupport?: string;
};

type GuidanceCard = {
  id: string;
  title: string;
  icon: LucideIcon;
  tone: string;
  body?: string;
  items?: string[];
  ordered?: boolean;
};

export function getMoonHubStepIcon(step: TreatmentStepConfig): LucideIcon {
  return STEP_ICONS[step.id] ?? STEP_ICONS[step.contentType] ?? BookOpen;
}

// Re-exported so existing importers of this module keep working.
export { getMoonHubStepPhase };

export function getMoonHubGuidanceContent({
  t,
  step,
  perspective,
  cycleContext,
}: {
  t: TranslationFunction;
  step: TreatmentStepConfig;
  perspective: StepPerspective;
  cycleContext?: CycleContentContext;
}): GuidanceContent | null {
  const stepContent = getStepContentOverride(t, step, cycleContext);
  const phaseName = getMoonHubStepPhase(step.id);
  const phaseMap = getMoonPhaseContentMap(t);
  const basePhaseContent = phaseMap[phaseName]
    ? getMoonPhaseContent(phaseName, t, cycleContext)
    : null;
  const perspectiveContent = basePhaseContent
    ? getMoonPhaseContentForPerspective(phaseName, perspective, t)
    : null;
  const phaseContent =
    perspective === "self"
      ? basePhaseContent
      : basePhaseContent && perspectiveContent
        ? { ...basePhaseContent, ...perspectiveContent }
        : perspectiveContent;

  if (!phaseContent && !stepContent) return null;
  return {
    ...phaseContent,
    ...stepContent,
    whenToCall: stepContent?.whenToCall ?? phaseContent?.whenToCall ?? [],
    emotionalSupport:
      stepContent?.emotionalSupport ?? phaseContent?.emotionalSupport,
  };
}

export function MoonHubGuidanceCarousel({
  step,
  stepColor,
  perspective,
  cycleContext,
  measureKey,
}: {
  step: TreatmentStepConfig;
  stepColor: string;
  perspective: StepPerspective;
  cycleContext?: CycleContentContext;
  measureKey: string;
}) {
  const t = useTranslations();
  const cards = buildGuidanceCards(
    t,
    getMoonHubGuidanceContent({ t, step, perspective, cycleContext }),
    stepColor,
  );

  if (cards.length === 0) return null;

  return (
    <View style={{ paddingTop: 14, paddingBottom: 4 }}>
      <HorizontalSnapCarousel
        items={cards}
        gap={10}
        fallbackCardWidth={244}
        getCardWidth={(containerWidth) =>
          Math.min(268, Math.max(226, containerWidth * 0.78))
        }
        keyExtractor={(card) => card.id}
        measureKey={measureKey}
        equalizeItemHeights
        contentContainerStyle={{ paddingLeft: 0, paddingRight: 0 }}
        renderItem={(card, cardWidth, _index, equalizedHeight) => (
          <GuidanceInfoCard
            card={card}
            width={cardWidth}
            equalizedHeight={equalizedHeight}
          />
        )}
      />
    </View>
  );
}

function buildGuidanceCards(
  t: TranslationFunction,
  content: GuidanceContent | null,
  stepColor: string,
): GuidanceCard[] {
  if (!content) return [];
  const cards: GuidanceCard[] = [];

  if (content.howItWorks) {
    cards.push({
      id: "overview",
      title: t("care.guidance.cards.how_it_works"),
      icon: BookOpen,
      tone: stepColor,
      body: content.howItWorks,
    });
  }

  if ((content.whatToExpect?.length ?? 0) > 0) {
    cards.push({
      id: "expect",
      title: t("care.guidance.cards.what_to_expect"),
      icon: Info,
      tone: TEXT_MID,
      items: content.whatToExpect,
    });
  }

  if ((content.prepInstructions?.length ?? 0) > 0) {
    cards.push({
      id: "prepare",
      title: t("care.guidance.cards.how_to_prepare"),
      icon: ClipboardList,
      tone: TEXT_MID,
      items: content.prepInstructions,
      ordered: true,
    });
  }

  if ((content.whenToCall?.length ?? 0) > 0) {
    cards.push({
      id: "call",
      title: t("care.guidance.cards.when_to_call"),
      icon: AlertTriangle,
      tone: WARNING,
      items: content.whenToCall,
    });
  }

  return cards;
}

function GuidanceInfoCard({
  card,
  width,
  equalizedHeight,
}: {
  card: GuidanceCard;
  width: number;
  equalizedHeight?: number;
}) {
  const Icon = card.icon;
  return (
    <View
      style={{
        width,
        minHeight: CARD_MIN_HEIGHT,
        ...(equalizedHeight
          ? { height: Math.max(equalizedHeight, CARD_MIN_HEIGHT) }
          : null),
        borderRadius: 16,
        borderWidth: 1,
        borderColor: "rgba(92,52,32,0.1)",
        backgroundColor: "rgba(255,255,255,0.58)",
        padding: 14,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          marginBottom: 12,
        }}
      >
        <Icon size={13} color={TEXT_LIGHT} strokeWidth={1.75} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 10,
            lineHeight: 13,
            color: TEXT_LIGHT,
            fontWeight: "800",
            letterSpacing: 0.4,
            textTransform: "uppercase",
          }}
        >
          {card.title}
        </Text>
      </View>

      {card.body ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 18,
            fontWeight: "500",
            color: TEXT_MID,
          }}
        >
          {card.body}
        </Text>
      ) : null}

      {card.items ? (
        <View style={{ gap: 8 }}>
          {card.items.map((item, index) => (
            <GuidanceItemRow
              key={`${card.id}-${index}`}
              text={item}
              index={card.ordered ? index : undefined}
            />
          ))}
        </View>
      ) : null}
    </View>
  );
}

function GuidanceItemRow({ text, index }: { text: string; index?: number }) {
  const Icon = getGuidanceItemIcon(text);
  return (
    <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 8 }}>
      <View style={{ width: 16, alignItems: "center", paddingTop: 1 }}>
        {typeof index === "number" ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              lineHeight: 15,
              fontWeight: "800",
              color: TEXT_MID,
            }}
          >
            {index + 1}.
          </Text>
        ) : (
          <Icon size={14} color={TEXT_MID} strokeWidth={1.7} />
        )}
      </View>
      <Text
        className="font-dm-sans"
        style={{
          flex: 1,
          fontSize: 12,
          lineHeight: 18,
          fontWeight: "500",
          color: TEXT_MID,
        }}
      >
        {text}
      </Text>
    </View>
  );
}

function getGuidanceItemIcon(text: string): LucideIcon {
  const normalized = text.toLowerCase();
  if (/blood|draw|lab|hormone|estradiol|fsh|lh|progesterone/.test(normalized)) {
    return Droplet;
  }
  if (/ultrasound|scan|follicle|lining|monitor/.test(normalized)) {
    return Activity;
  }
  if (
    /medication|prescription|pharmacy|injection|trigger|estrogen|pill|lupron|birth control/.test(
      normalized,
    )
  ) {
    return Pill;
  }
  if (/embryo|fertiliz|blastocyst|egg|sperm|sample/.test(normalized)) {
    return Beaker;
  }
  if (/transfer|pregnancy|implant/.test(normalized)) {
    return Baby;
  }
  if (/pain|bleeding|fever|severe|shortness|call|clinic/.test(normalized)) {
    return AlertTriangle;
  }
  if (/rest|hydrated|sleep|emotional|nervous|support/.test(normalized)) {
    return Heart;
  }
  if (/calendar|schedule|timing|delivery|date|week|day/.test(normalized)) {
    return Calendar;
  }
  return Check;
}
